import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const posts = [
  {
    slug: 'reasons-airbnb-not-getting-booked',
    title: '5 Reasons Your Airbnb Listing Isn\'t Getting Booked (And How to Fix It)',
    excerpt:
      'Most hosts blame slow seasons or bad luck. The truth? Your listing is likely making avoidable mistakes that push guests to competitors. Here\'s what to fix today.',
    category: 'Listing Optimization',
    readTime: '5 min read',
    date: 'June 10, 2025',
    image:
      'https://public.readdy.ai/ai/img_res/fdbdc4964ed6687a2d2529b34137c5eb.jpg',
  },
  {
    slug: 'empty-property-to-5-star-listing',
    title: 'From Empty Property to 5-Star Listing: Our End-to-End Setup Process',
    excerpt:
      'We\'ve launched dozens of listings from scratch. Here\'s the exact step-by-step process we use to turn a bare property into a fully booked, 5-star Airbnb.',
    category: 'Setup & Launch',
    readTime: '6 min read',
    date: 'May 28, 2025',
    image:
      'https://readdy.ai/api/search-image?query=beautiful-airbnb-property-transformation-before-and-after-professional-interior-design-staging-and-setup-modern-furnished-short-term-rental-ready-for-guests-with-elegant-decor-and-welcoming-atmosphere&width=800&height=500&seq=blog-card-2&orientation=landscape',
  },
  {
    slug: 'hosting-on-hard-mode-lagos',
    title: 'Hosting on Hard Mode: What Running Airbnb in Lagos Taught Us About 5-Star Hospitality',
    excerpt:
      'Lagos is one of the most operationally challenging cities in the world for short-term rentals. Here\'s how we maintained a 4.95 rating anyway — and what it means for your hosting.',
    category: 'Our Story',
    readTime: '7 min read',
    date: 'May 14, 2025',
    image:
      'https://readdy.ai/api/search-image?query=vibrant-lagos-nigeria-cityscape-at-sunset-with-modern-buildings-and-busy-streets-showing-dynamic-african-city-energy-professional-urban-photography-with-warm-golden-light-and-contemporary-architecture&width=800&height=500&seq=blog-card-3&orientation=landscape',
  },
];

const categoryColors: Record<string, string> = {
  'Listing Optimization': 'bg-amber-100 text-amber-900',
  'Setup & Launch': 'bg-emerald-100 text-emerald-900',
  'Our Story': 'bg-blue-100 text-blue-900',
};

export default function BlogPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block bg-amber-100 text-amber-900 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            Insights & Resources
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            The Upgraded Version<br />
            <span className="text-amber-900">Blog</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Practical strategies, real stories, and expert insights to help you build a more profitable short-term rental business.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <Link to={`/blog/${posts[0].slug}`} className="group block">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow">
              <div className="relative h-72 lg:h-auto overflow-hidden">
                <img
                  src={posts[0].image}
                  alt={posts[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="bg-amber-900 p-10 flex flex-col justify-center">
                <span className="inline-block bg-white/20 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 w-fit">
                  Featured
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight group-hover:text-amber-200 transition-colors">
                  {posts[0].title}
                </h2>
                <p className="text-amber-100 mb-6 leading-relaxed text-sm">
                  {posts[0].excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-amber-200 text-xs">
                    <span>{posts[0].date}</span>
                    <span>·</span>
                    <span>{posts[0].readTime}</span>
                  </div>
                  <span className="inline-flex items-center text-white text-sm font-medium">
                    Read More
                    <i className="ri-arrow-right-line ml-1"></i>
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* All Posts */}
      <section className="py-12 pb-24">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">All Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:border-amber-300 transition-all"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-3 ${categoryColors[post.category]}`}>
                    {post.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug group-hover:text-amber-900 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-500 text-sm mb-4 leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{post.date}</span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-amber-900 to-amber-950">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Put These Strategies to Work?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Let's talk about your property and how we can help you maximize its potential.
          </p>
          <Link
            to="/get-started"
            className="inline-flex items-center px-10 py-4 bg-white text-amber-900 rounded-full text-base font-medium hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
          >
            Get Started Today
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
